import React, { useState, useEffect, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import {
  Phone,
  MapPin,
  ChevronDown,
  Menu,
  X,
  ArrowRight,
  Quote,
  CheckCircle2,
  Users,
  Shield,
  Activity,
  Microscope,
  FlaskConical,
  Stethoscope,
  Building2,
  Target,
  Lightbulb,
  Heart,
  ExternalLink,
  MessageCircle,
} from 'lucide-react';
import {
  heroData,
  challengeData,
  scientificData,
  partnershipData,
  governanceData,
  visionData,
  ctaData,
  stats,
  locations,
  testimonials,
  contactInfo,
} from '../data/mock';

const LOGO_URL = "../images/logo_raho.png";
const IMI_LOGO_URL = "../images/logo_imi.png";

// Animation variants
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInLeft = {
  hidden: { opacity: 0, x: -40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const fadeInRight = {
  hidden: { opacity: 0, x: 40 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.6, ease: "easeOut" } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

const scaleIn = {
  hidden: { opacity: 0, scale: 0.9 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.4 } }
};

// Animated Section Component
const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={staggerContainer}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Counter Animation Component
const AnimatedCounter = ({ value, suffix = '' }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });
  
  useEffect(() => {
    if (isInView) {
      const numValue = parseInt(value.replace(/\D/g, ''));
      const duration = 2000;
      const steps = 60;
      const increment = numValue / steps;
      let current = 0;
      
      const timer = setInterval(() => {
        current += increment;
        if (current >= numValue) {
          setCount(numValue);
          clearInterval(timer);
        } else {
          setCount(Math.floor(current));
        }
      }, duration / steps);
      
      return () => clearInterval(timer);
    }
  }, [isInView, value]);
  
  return (
    <span ref={ref}>
      {count.toLocaleString()}{suffix}
    </span>
  );
};

const LandingPage = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setMobileMenuOpen(false);
  };

  const navItems = ['Tentang', 'Sains', 'Partnership', 'Lokasi', 'Testimoni'];

  return (
    <div className="min-h-screen bg-[#0F172A]">
      {/* Header */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          isScrolled
            ? 'bg-[#0F172A]/95 backdrop-blur-md shadow-lg shadow-black/20'
            : 'bg-transparent'
        }`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              className="flex items-center gap-3"
            >
              <img 
                src={LOGO_URL} 
                alt="RAHO Club Premier" 
                className="h-12 w-auto object-contain"
              />
            </motion.div>

            <nav className="hidden md:flex items-center gap-8">
              {navItems.map((item, index) => (
                <motion.button
                  key={item}
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="text-[#94A3B8] hover:text-[#F59E0B] transition-colors font-medium"
                >
                  {item}
                </motion.button>
              ))}
            </nav>

            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="hidden md:block"
            >
              <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#D97706] hover:to-[#B45309] text-[#0F172A] font-semibold shadow-lg hover:shadow-[#F59E0B]/25 transition-all px-6">
                  <Phone className="w-4 h-4 mr-2" />
                  Hubungi Kami
                </Button>
              </a>
            </motion.div>

            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? (
                <X className="w-6 h-6 text-[#F8FAFC]" />
              ) : (
                <Menu className="w-6 h-6 text-[#F8FAFC]" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="md:hidden bg-[#0F172A] border-t border-[#1E293B]"
          >
            <div className="px-4 py-4 space-y-3">
              {navItems.map((item) => (
                <button
                  key={item}
                  onClick={() => scrollToSection(item.toLowerCase())}
                  className="block w-full text-left text-[#94A3B8] hover:text-[#F59E0B] py-2 font-medium"
                >
                  {item}
                </button>
              ))}
              <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" className="block pt-2">
                <Button className="w-full bg-gradient-to-r from-[#F59E0B] to-[#D97706] text-[#0F172A] font-semibold">
                  <Phone className="w-4 h-4 mr-2" />
                  Hubungi Kami
                </Button>
              </a>
            </div>
          </motion.div>
        )}
      </header>

      {/* HERO SECTION */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 bg-gradient-to-b from-[#0F172A] via-[#0F172A] to-[#1E293B]" />
        
        {/* Animated Orbs */}
        <div className="absolute top-1/4 left-1/4 w-[600px] h-[600px] bg-[#F59E0B]/10 rounded-full blur-[150px] animate-pulse" />
        <div className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] bg-[#3B82F6]/10 rounded-full blur-[120px] animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-[#F59E0B]/5 rounded-full blur-[200px] animate-pulse" style={{ animationDelay: '2s' }} />
        
        {/* Floating Particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(20)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-[#F59E0B]/30 rounded-full"
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.8, 0.3],
                scale: [1, 1.2, 1],
              }}
              transition={{
                duration: 3 + Math.random() * 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
            />
          ))}
        </div>
        
        {/* Grid Pattern Overlay */}
        <div className="absolute inset-0 bg-[linear-gradient(rgba(245,158,11,0.03)_1px,transparent_1px),linear-gradient(90deg,rgba(245,158,11,0.03)_1px,transparent_1px)] bg-[size:60px_60px]" />
        
        {/* Decorative Lines */}
        <div className="absolute top-20 left-10 w-32 h-px bg-gradient-to-r from-transparent via-[#F59E0B]/50 to-transparent" />
        <div className="absolute top-40 right-20 w-48 h-px bg-gradient-to-r from-transparent via-[#F59E0B]/30 to-transparent" />
        <div className="absolute bottom-40 left-20 w-40 h-px bg-gradient-to-r from-transparent via-[#F59E0B]/40 to-transparent" />
        
        {/* Corner Decorations */}
        <div className="absolute top-24 left-8 w-20 h-20 border-l-2 border-t-2 border-[#F59E0B]/20 rounded-tl-3xl" />
        <div className="absolute top-24 right-8 w-20 h-20 border-r-2 border-t-2 border-[#F59E0B]/20 rounded-tr-3xl" />
        <div className="absolute bottom-24 left-8 w-20 h-20 border-l-2 border-b-2 border-[#F59E0B]/20 rounded-bl-3xl" />
        <div className="absolute bottom-24 right-8 w-20 h-20 border-r-2 border-b-2 border-[#F59E0B]/20 rounded-br-3xl" />

        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20 text-center">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-8xl font-black text-[#F8FAFC] mb-4 leading-none tracking-tight">
              {heroData.title}
            </h1>
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-medium text-[#F59E0B] mb-8 italic">
              {heroData.subtitle}
            </h2>
          </motion.div>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.5 }}
            className="text-lg sm:text-xl text-[#94A3B8] leading-relaxed mb-6 max-w-4xl mx-auto"
          >
            {heroData.description}
          </motion.p>

          {/* Enhanced Tagline with Highlighted Words */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mb-10 max-w-3xl mx-auto"
          >
            <p className="text-xl sm:text-2xl text-[#F8FAFC] font-medium leading-relaxed">
              Bukan sekadar meredakan gejala — kami menyelami{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#F59E0B] font-bold">akar masalah</span>
                <span className="absolute bottom-0 left-0 w-full h-2 bg-[#F59E0B]/20 -skew-x-6" />
              </span>
              {' '}dan bekerja hingga{' '}
              <span className="relative inline-block">
                <span className="relative z-10 text-[#F59E0B] font-bold">tingkat sel</span>
                <span className="absolute bottom-0 left-0 w-full h-2 bg-[#F59E0B]/20 -skew-x-6" />
              </span>
              .
            </p>
          </motion.div>

          {/* CTA Button */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.7 }}
            className="mb-12"
          >
            <Button
              size="lg"
              onClick={() => scrollToSection('lokasi')}
              className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#D97706] hover:to-[#B45309] text-[#0F172A] font-bold shadow-xl hover:shadow-2xl hover:shadow-[#F59E0B]/30 transition-all text-lg px-8 py-6 rounded-xl"
              data-testid="hero-lokasi-btn"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Lihat Cabang Raho Club Premier
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </motion.div>

          {/* Stats */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="grid grid-cols-3 gap-6 max-w-2xl mx-auto"
          >
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-3xl sm:text-4xl font-bold text-[#F59E0B]">
                  <AnimatedCounter value={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-[#94A3B8] mt-1">{stat.label}</div>
              </div>
            ))}
          </motion.div>

          {/* Scroll Indicator */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.2 }}
            className="absolute bottom-8 left-1/2 -translate-x-1/2"
          >
            <motion.div
              animate={{ y: [0, 10, 0] }}
              transition={{ duration: 2, repeat: Infinity }}
              className="text-[#94A3B8]"
            >
              <ChevronDown className="w-8 h-8" />
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CHALLENGE SECTION */}
      <section id="tentang" className="py-24 bg-[#F8FAFC] relative overflow-hidden">
        <AnimatedSection className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#DC2626]/10 text-[#DC2626] rounded-full px-4 py-2 mb-6">
              <Target className="w-4 h-4" />
              <span className="text-sm font-semibold">Tantangan Modern</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-6 max-w-4xl mx-auto">
              {challengeData.title}
            </motion.h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-12">
            {/* Challenges */}
            <motion.div variants={fadeInLeft}>
              <h3 className="text-xl font-bold text-[#0F172A] mb-6">{challengeData.subtitle}</h3>
              <div className="space-y-4">
                {challengeData.challenges.map((challenge, index) => (
                  <motion.div
                    key={index}
                    variants={scaleIn}
                    className="flex items-center gap-4 bg-white rounded-xl p-4 shadow-sm border border-[#E2E8F0]"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#DC2626]/10 flex items-center justify-center">
                      <X className="w-5 h-5 text-[#DC2626]" />
                    </div>
                    <p className="text-[#334155]">{challenge}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            {/* Solutions */}
            <motion.div variants={fadeInRight}>
              <h3 className="text-xl font-bold text-[#0F172A] mb-6">{challengeData.solutionIntro}</h3>
              <div className="space-y-4">
                {challengeData.solutions.map((solution, index) => (
                  <motion.div
                    key={index}
                    variants={scaleIn}
                    className="flex items-center gap-4 bg-gradient-to-r from-[#F59E0B]/10 to-transparent rounded-xl p-4 border border-[#F59E0B]/20"
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                      <CheckCircle2 className="w-5 h-5 text-[#F59E0B]" />
                    </div>
                    <p className="text-[#334155] font-medium">{solution}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </AnimatedSection>
      </section>

      {/* SCIENTIFIC FOUNDATION SECTION */}
      <section id="sains" className="py-24 bg-[#0F172A] relative overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#F59E0B]/5 rounded-full blur-[150px]" />
        
        <AnimatedSection className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#1E293B] text-[#F59E0B] rounded-full px-4 py-2 mb-6">
              <Microscope className="w-4 h-4" />
              <span className="text-sm font-semibold">{scientificData.title}</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
              {scientificData.subtitle}
            </motion.h2>
          </div>

          {/* Core Platforms */}
          <motion.div variants={staggerContainer} className="grid md:grid-cols-3 gap-6 mb-16">
            {scientificData.platforms.map((platform) => (
              <motion.div
                key={platform.id}
                variants={scaleIn}
                whileHover={{ y: -5, scale: 1.02 }}
                className="bg-gradient-to-br from-[#1E293B] to-[#0F172A] rounded-2xl p-6 border border-[#334155] hover:border-[#F59E0B]/50 transition-all shadow-xl"
              >
                <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center mb-4">
                  {platform.icon === 'Microscope' && <Microscope className="w-7 h-7 text-[#F59E0B]" />}
                  {platform.icon === 'FlaskConical' && <FlaskConical className="w-7 h-7 text-[#F59E0B]" />}
                  {platform.icon === 'Activity' && <Activity className="w-7 h-7 text-[#F59E0B]" />}
                </div>
                <h3 className="text-xl font-bold text-[#F8FAFC] mb-3">{platform.name}</h3>
                <p className="text-[#94A3B8] leading-relaxed">{platform.description}</p>
              </motion.div>
            ))}
          </motion.div>

          {/* Applications */}
          <motion.div variants={fadeInUp} className="bg-[#1E293B]/50 rounded-2xl p-8 border border-[#334155]">
            <h3 className="text-xl font-bold text-[#F8FAFC] mb-6 text-center">{scientificData.applicationsTitle}</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {scientificData.applications.map((app, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  className="flex items-center gap-3 bg-[#0F172A] rounded-lg p-4 border border-[#334155]"
                >
                  <Heart className="w-5 h-5 text-[#F59E0B] flex-shrink-0" />
                  <span className="text-[#F8FAFC]">{app}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* PARTNERSHIP SECTION */}
      <section id="partnership" className="py-24 bg-[#F8FAFC] relative overflow-hidden">
        <AnimatedSection className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#F59E0B]/10 text-[#D97706] rounded-full px-4 py-2 mb-6">
              <Users className="w-4 h-4" />
              <span className="text-sm font-semibold">{partnershipData.subtitle}</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-3xl lg:text-4xl font-bold text-[#0F172A] mb-4 max-w-4xl mx-auto">
              {partnershipData.title}
            </motion.h2>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* For Doctors */}
            <motion.div variants={fadeInLeft}>
              <Card className="h-full border-2 border-[#F59E0B]/30 hover:border-[#F59E0B] transition-colors shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#F59E0B]/20 flex items-center justify-center">
                      <Stethoscope className="w-7 h-7 text-[#F59E0B]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0F172A]">{partnershipData.forDoctors.title}</h3>
                  </div>
                  <div className="space-y-3">
                    {partnershipData.forDoctors.items.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                        <p className="text-[#334155]">{item}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>

            {/* For Institutions */}
            <motion.div variants={fadeInRight}>
              <Card className="h-full border-2 border-[#3B82F6]/30 hover:border-[#3B82F6] transition-colors shadow-lg">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-6">
                    <div className="w-14 h-14 rounded-xl bg-[#3B82F6]/20 flex items-center justify-center">
                      <Building2 className="w-7 h-7 text-[#3B82F6]" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0F172A]">{partnershipData.forInstitutions.title}</h3>
                  </div>
                  <div className="space-y-3">
                    {partnershipData.forInstitutions.items.map((item, index) => (
                      <div key={index} className="flex items-start gap-3">
                        <CheckCircle2 className="w-5 h-5 text-[#3B82F6] mt-0.5 flex-shrink-0" />
                        <p className="text-[#334155]">{item}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>

          {/* Partnership CTA Button */}
          <motion.div variants={fadeInUp} className="text-center mb-16">
            <a href="https://wa.me/6281806977029" target="_blank" rel="noopener noreferrer">
              <Button
                size="lg"
                className="bg-gradient-to-r from-[#F59E0B] to-[#D97706] hover:from-[#D97706] hover:to-[#B45309] text-[#0F172A] font-bold shadow-xl hover:shadow-2xl transition-all text-lg px-10 py-6 rounded-xl hover:scale-105"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Become a Research Partner
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </a>
          </motion.div>

          {/* Governance */}
          <motion.div variants={fadeInUp} className="bg-[#0F172A] rounded-2xl p-8">
            <h3 className="text-2xl font-bold text-[#F8FAFC] mb-6 flex items-center gap-3">
              <Shield className="w-6 h-6 text-[#F59E0B]" />
              {governanceData.title}
            </h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {governanceData.items.map((item, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#F59E0B] mt-0.5 flex-shrink-0" />
                  <p className="text-[#94A3B8]">{item}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </AnimatedSection>
      </section>

      {/* LOCATIONS SECTION */}
      <section id="lokasi" className="py-24 bg-[#0F172A] relative overflow-hidden">
        <AnimatedSection className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#1E293B] text-[#F59E0B] rounded-full px-4 py-2 mb-6">
              <MapPin className="w-4 h-4" />
              <span className="text-sm font-semibold">List Partnership</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold text-[#F8FAFC] mb-4">
              Cabang Raho Club Premier
            </motion.h2>
          </div>

          <motion.div variants={staggerContainer} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {locations.map((location) => (
              <motion.div
                key={location.id}
                variants={scaleIn}
                whileHover={{ y: -5 }}
                className="bg-[#1E293B] rounded-xl p-6 border border-[#334155] hover:border-[#F59E0B]/50 transition-all"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 rounded-full bg-[#F59E0B]/20 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#F59E0B]" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-[#F8FAFC]">{location.city}</h3>
                    <p className="text-sm text-[#F59E0B]">{location.region}</p>
                  </div>
                </div>
                <p className="text-[#F8FAFC] font-medium mb-2">{location.name}</p>
                <p className="text-sm text-[#94A3B8] leading-relaxed">{location.address}</p>
              </motion.div>
            ))}
          </motion.div>
        </AnimatedSection>
      </section>

      {/* TESTIMONIALS SECTION */}
      <section id="testimoni" className="py-24 bg-[#F8FAFC] relative overflow-hidden">
        <AnimatedSection className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <motion.div variants={fadeInUp} className="inline-flex items-center gap-2 bg-[#F59E0B]/10 text-[#D97706] rounded-full px-4 py-2 mb-6">
              <Quote className="w-4 h-4" />
              <span className="text-sm font-semibold">Testimoni</span>
            </motion.div>
            <motion.h2 variants={fadeInUp} className="text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4">
              Kisah Kesembuhan Member
            </motion.h2>
          </div>

          <motion.div variants={staggerContainer} className="grid md:grid-cols-2 gap-8">
            {testimonials.map((testimonial) => (
              <motion.div
                key={testimonial.id}
                variants={scaleIn}
                className="bg-white rounded-2xl p-8 shadow-xl border border-[#E2E8F0]"
              >
                <div className="flex items-start gap-4 mb-6">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full object-cover border-2 border-[#F59E0B]"
                  />
                  <div>
                    <h3 className="text-lg font-bold text-[#0F172A]">{testimonial.name}</h3>
                    <p className="text-[#F59E0B] font-medium">{testimonial.condition}</p>
                  </div>
                </div>
                <div className="relative">
                  <Quote className="absolute -top-2 -left-2 w-8 h-8 text-[#F59E0B]/20" />
                  <p className="text-[#475569] leading-relaxed italic pl-6">
                    "{testimonial.quote}"
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </AnimatedSection>
      </section>

      {/* IMI SECTION - Centered */}
      <section className="py-16 bg-[#0F172A]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-[#94A3B8] mb-4">Research komunitas untuk</p>
            <a href={contactInfo.imiWebsite} target="_blank" rel="noopener noreferrer" className="inline-block mb-4">
              <img 
                src={IMI_LOGO_URL}
                alt="Institut Molekul Indonesia"
                className="h-20 w-auto hover:opacity-80 transition-opacity"
              />
            </a>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="bg-[#0F172A] border-t border-[#1E293B] py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="RAHO Club Premier" className="h-10 w-auto" />
            </div>
            <p className="text-[#94A3B8] text-sm text-center">
              © {new Date().getFullYear()} RAHO Club Premier. All rights reserved.
            </p>
            <div className="flex items-center gap-4">
              <a href={contactInfo.whatsappLink} target="_blank" rel="noopener noreferrer" className="text-[#94A3B8] hover:text-[#F59E0B] transition-colors">
                <Phone className="w-5 h-5" />
              </a>
            </div>
          </div>
        </div>
      </footer>

      {/* Floating WhatsApp Button */}
      <a
        href={contactInfo.whatsappLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 bg-[#25D366] hover:bg-[#128C7E] text-white p-4 rounded-full shadow-2xl hover:scale-110 transition-all"
        data-testid="floating-whatsapp-btn"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>
    </div>
  );
};

export default LandingPage;
